import React, { createContext, useState, useEffect, useCallback } from 'react';
import i18n from '../i18n/config';
import authService from '../services/authService';
import { resolveTimezone } from '../constants/timezones';
import { canAccess, canManage, hasPermission } from '../utils/permissions';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timezone, setTimezoneState] = useState(() =>
    resolveTimezone(localStorage.getItem('timezone'))
  );

  /**
   * Settle on the effective timezone for a user and persist it.
   *
   * When the account has none saved, the browser's zone is detected and
   * written back once, so the backend can interpret date filters for this
   * user even on requests it did not originate.
   */
  const syncTimezone = useCallback(async (userData, authToken) => {
    const resolved = resolveTimezone(userData?.timezone);
    setTimezoneState(resolved);
    localStorage.setItem('timezone', resolved);

    if (!userData?.timezone && authToken) {
      try {
        await authService.updatePreferences(authToken, { timezone: resolved });
      } catch (err) {
        // A failed preference write must never block signing in; the header
        // still carries the resolved zone and the next load retries.
        console.error('Failed to save timezone preference:', err);
      }
    }
  }, []);

  const setTimezone = useCallback(async (zone) => {
    const resolved = resolveTimezone(zone);
    setTimezoneState(resolved);
    localStorage.setItem('timezone', resolved);

    if (token) {
      try {
        await authService.updatePreferences(token, { timezone: resolved });
      } catch (err) {
        console.error('Failed to save timezone preference:', err);
      }
    }
  }, [token]);

  useEffect(() => {
    // Load language from localStorage on mount
    const savedLanguage = localStorage.getItem('language') || 'en';
    i18n.changeLanguage(savedLanguage);

    if (token) {
      authService.getCurrentUser(token)
        .then(user => {
          setUser(user);
          // Set language based on user preference from backend
          const userLanguage = user.language === 'en' ? 'en' : 'es_MX';
          i18n.changeLanguage(userLanguage);
          localStorage.setItem('language', userLanguage);
          syncTimezone(user, token);
        })
        .catch(err => {
          localStorage.removeItem('authToken');
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, syncTimezone]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await authService.login(email, password);
      const { token: newToken, user: userData } = response;

      localStorage.setItem('authToken', newToken);
      setToken(newToken);
      setUser(userData);

      // Set language based on user preference
      const userLanguage = userData.language === 'en' ? 'en' : 'es_MX';
      i18n.changeLanguage(userLanguage);
      localStorage.setItem('language', userLanguage);

      await syncTimezone(userData, newToken);

      return userData;
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    }
  }, [syncTimezone]);

  const logout = useCallback(async () => {
    try {
      await authService.logout(token);
    } catch (err) {
      console.error('Logout error:', err);
    }
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
  }, [token]);

  const value = {
    user,
    token,
    loading,
    error,
    login,
    logout,
    timezone,
    setTimezone,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.is_staff || false,
    // The flat list the session returns. Guards read capabilities from it
    // rather than role names, so adding a role never means editing a route.
    permissions: user?.permissions || [],
    roles: user?.roles || [],
    can: (permission) => hasPermission(user?.permissions, permission),
    canAccess: (module) => canAccess(user?.permissions, module),
    canManage: (module) => canManage(user?.permissions, module),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
